import { authRequest, authUploadFormData, clearAuthStorage, AuthApiError } from '@/lib/api/auth-client';
import { AUTH_STORAGE_KEYS, HRM_PRODUCT_CODE } from '@/lib/api/config';
import { resetSessionEnding } from '@/lib/auth/session-end';
import type {
  AuthSession,
  AuthProductAccess,
  LoginResponse,
  MeResponse,
  MyPermissionsResponse,
  MfaChallengeResponse,
  MfaStatus,
  SecurityDashboard,
} from '@/types/auth';
import { isMfaChallenge } from '@/types/auth';

export { isMfaChallenge };

export { AuthApiError };

function decodeJwtProductAccess(token: string | null): AuthProductAccess[] {
  if (!token) return [];
  try {
    const payload = token.split('.')[1];
    if (!payload) return [];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const decoded = JSON.parse(json) as { products?: AuthProductAccess[] };
    return decoded.products ?? [];
  } catch {
    return [];
  }
}

function legacyPermissionsFromJwt(token: string | null): AuthProductAccess[] {
  const fromJwt = decodeJwtProductAccess(token);
  const hrm = fromJwt.find((p) => p.code.toUpperCase() === HRM_PRODUCT_CODE);
  if (!hrm?.permissions?.length) return [];
  return fromJwt;
}

function persistLoginSession(data: LoginResponse, orgCode: string): void {
  localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, data.accessToken);
  if (data.refreshToken) {
    localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, data.refreshToken);
  } else {
    localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
  }
  localStorage.setItem(AUTH_STORAGE_KEYS.sessionId, data.sessionId);
  if (data.deviceName != null && String(data.deviceName).length > 0) {
    localStorage.setItem(AUTH_STORAGE_KEYS.deviceName, String(data.deviceName));
  } else {
    localStorage.removeItem(AUTH_STORAGE_KEYS.deviceName);
  }
  localStorage.setItem(AUTH_STORAGE_KEYS.orgCode, orgCode.trim());
  resetSessionEnding();
}

export async function loginWithCredentials(input: {
  email: string;
  password: string;
  orgCode: string;
}): Promise<LoginResponse | MfaChallengeResponse> {
  const envelope = await authRequest<LoginResponse | MfaChallengeResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({
      email: input.email.trim(),
      password: input.password,
      orgCode: input.orgCode.trim(),
      productCode: HRM_PRODUCT_CODE,
    }),
  });

  if (!envelope.data) {
    throw new Error('Login returned no data');
  }

  if (isMfaChallenge(envelope.data)) {
    return envelope.data;
  }

  persistLoginSession(envelope.data, input.orgCode);
  return envelope.data;
}

export async function verifyMfaLogin(input: {
  mfaToken: string;
  code: string;
  orgCode: string;
  method?: 'totp' | 'email' | 'backup';
}): Promise<LoginResponse> {
  const envelope = await authRequest<LoginResponse>('/auth/mfa/verify', {
    method: 'POST',
    body: JSON.stringify({
      mfaToken: input.mfaToken,
      code: input.code.trim(),
      method: input.method,
    }),
  });
  if (!envelope.data?.accessToken) {
    throw new Error('Two-factor verification returned no session');
  }
  persistLoginSession(envelope.data, input.orgCode);
  return envelope.data;
}

export async function resendMfaLoginEmail(mfaToken: string): Promise<{ sent: boolean; maskedEmail: string }> {
  const envelope = await authRequest<{ sent: boolean; maskedEmail: string }>('/auth/mfa/resend-email', {
    method: 'POST',
    body: JSON.stringify({ mfaToken }),
  });
  return envelope.data ?? { sent: true, maskedEmail: '' };
}

export async function fetchMfaStatus(): Promise<MfaStatus> {
  const envelope = await authRequest<MfaStatus>('/auth/mfa/status', { auth: true });
  if (!envelope.data) throw new Error('MFA status returned no data');
  return envelope.data;
}

export async function startTotpEnrollment(): Promise<{ secret: string; otpauthUrl: string }> {
  const envelope = await authRequest<{ secret: string; otpauthUrl: string }>('/auth/mfa/totp/start', {
    method: 'POST',
    auth: true,
  });
  if (!envelope.data?.secret) throw new Error('Could not start authenticator setup');
  return envelope.data;
}

export async function confirmTotpEnrollment(code: string): Promise<{
  backupCodes: string[];
  totpEnabled: boolean;
  emailEnabled: boolean;
}> {
  const envelope = await authRequest<{
    backupCodes: string[];
    totpEnabled: boolean;
    emailEnabled: boolean;
  }>('/auth/mfa/totp/confirm', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({ code: code.trim() }),
  });
  if (!envelope.data) throw new Error('Could not enable authenticator');
  return envelope.data;
}

export async function startEmailMfaEnrollment(): Promise<{ sent: boolean; maskedEmail: string }> {
  const envelope = await authRequest<{ sent: boolean; maskedEmail: string }>('/auth/mfa/email/start', {
    method: 'POST',
    auth: true,
  });
  return envelope.data ?? { sent: true, maskedEmail: '' };
}

export async function sendAuthenticatedMfaEmail(): Promise<{ sent: boolean; maskedEmail: string }> {
  const envelope = await authRequest<{ sent: boolean; maskedEmail: string }>('/auth/mfa/email/send', {
    method: 'POST',
    auth: true,
  });
  return envelope.data ?? { sent: true, maskedEmail: '' };
}

export async function confirmEmailMfaEnrollment(code: string): Promise<{
  totpEnabled: boolean;
  emailEnabled: boolean;
}> {
  const envelope = await authRequest<{ totpEnabled: boolean; emailEnabled: boolean }>(
    '/auth/mfa/email/confirm',
    {
      method: 'POST',
      auth: true,
      body: JSON.stringify({ code: code.trim() }),
    },
  );
  if (!envelope.data) throw new Error('Could not enable email two-factor');
  return envelope.data;
}

export async function disableMfa(input: {
  password: string;
  code: string;
  method?: 'totp' | 'email' | 'backup' | 'all';
}): Promise<MfaStatus> {
  const envelope = await authRequest<MfaStatus>('/auth/mfa/disable', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(input),
  });
  if (!envelope.data) throw new Error('Could not disable two-factor');
  return envelope.data;
}

/** Request a 6-digit OTP for password reset (auth-service `/auth/forgot-password`). */
export async function requestPasswordResetOtp(input: {
  email: string;
  orgCode: string;
}): Promise<{ sent: boolean }> {
  const envelope = await authRequest<{ sent: boolean }>('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({
      email: input.email.trim(),
      orgCode: input.orgCode.trim(),
      productCode: HRM_PRODUCT_CODE,
    }),
  });
  return envelope.data ?? { sent: true };
}

/** Verify OTP from email; returns a short-lived reset token (15 min). */
export async function verifyPasswordResetOtp(input: {
  email: string;
  orgCode: string;
  otp: string;
}): Promise<{ verified: boolean; resetToken: string; expiresIn: number }> {
  const envelope = await authRequest<{
    verified: boolean;
    resetToken: string;
    expiresIn: number;
  }>('/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({
      email: input.email.trim(),
      orgCode: input.orgCode.trim(),
      otp: input.otp.trim(),
    }),
  });
  if (!envelope.data?.resetToken) {
    throw new Error('OTP verification failed');
  }
  return envelope.data;
}

/** Set new password using reset token from verify-otp. */
export async function resetPasswordWithToken(input: {
  resetToken: string;
  newPassword: string;
}): Promise<{ updated: boolean }> {
  const envelope = await authRequest<{ updated: boolean }>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({
      resetToken: input.resetToken,
      newPassword: input.newPassword,
    }),
  });
  if (!envelope.data?.updated) {
    throw new Error('Password reset failed');
  }
  return envelope.data;
}

/** @deprecated Legacy one-step reset (OTP + password). Prefer verify + reset with token. */
export async function resetPasswordWithOtp(input: {
  email: string;
  orgCode: string;
  otp: string;
  newPassword: string;
}): Promise<{ updated: boolean }> {
  const envelope = await authRequest<{ updated: boolean }>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({
      email: input.email.trim(),
      orgCode: input.orgCode.trim(),
      otp: input.otp.trim(),
      newPassword: input.newPassword,
    }),
  });
  if (!envelope.data?.updated) {
    throw new Error('Password reset failed');
  }
  return envelope.data;
}

export type UserImagesPayload = {
  user: {
    profileImage?: string | null;
    coverImage?: string | null;
    profileImageUrl?: string | null;
    coverImageUrl?: string | null;
  };
};

export async function uploadMyProfileImages(input: {
  profileImage?: File;
  coverImage?: File;
}): Promise<UserImagesPayload['user']> {
  const form = new FormData();
  if (input.profileImage) {
    form.append('profile_image', input.profileImage);
  }
  if (input.coverImage) {
    form.append('cover_image', input.coverImage);
  }
  const envelope = await authUploadFormData<UserImagesPayload>('/auth/me/images', form);
  if (!envelope.data?.user) {
    throw new Error('Image upload returned no user data');
  }
  return envelope.data.user;
}

export async function fetchCurrentUser(): Promise<MeResponse['user']> {
  const envelope = await authRequest<MeResponse>('/auth/me', { auth: true });
  if (!envelope.data?.user) {
    throw new Error('Profile returned no user');
  }
  return envelope.data.user;
}

/** Effective permission codes — companion to slim JWT (not stored in the token). */
export async function fetchMyPermissions(
  productCode: string = HRM_PRODUCT_CODE,
): Promise<{ products: AuthProductAccess[]; rolePreview: MyPermissionsResponse['rolePreview'] }> {
  const params = new URLSearchParams();
  if (productCode.trim()) params.set('productCode', productCode.trim());
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const envelope = await authRequest<MyPermissionsResponse & { permissions?: string[] }>(`/auth/me/permissions${suffix}`, {
    auth: true,
  });

  // Handle case where backend returns a flat `permissions` array directly on the root
  if (envelope.data && !envelope.data.products && Array.isArray(envelope.data.permissions)) {
    return {
      products: [{ code: HRM_PRODUCT_CODE, plan: 'default', permissions: envelope.data.permissions }],
      rolePreview: envelope.data.rolePreview ?? null,
    };
  }

  return {
    products: envelope.data?.products ?? [],
    rolePreview: envelope.data?.rolePreview ?? null,
  };
}

/**
 * Activate server-side role preview so FE + HRM API guards use that role's
 * exact cumulative permissions (shared Redis `perms:` cache).
 */
export async function setSessionRolePreview(input: {
  orgRoleId: string;
  orgRoleLevelId?: string | null;
}): Promise<{
  rolePreview: NonNullable<MyPermissionsResponse['rolePreview']>;
  products: AuthProductAccess[];
}> {
  const envelope = await authRequest<{
    rolePreview: NonNullable<MyPermissionsResponse['rolePreview']>;
    products: AuthProductAccess[];
  }>('/auth/me/role-preview', {
    method: 'POST',
    auth: true,
    body: JSON.stringify({
      orgRoleId: input.orgRoleId,
      orgRoleLevelId: input.orgRoleLevelId ?? null,
    }),
  });
  if (!envelope.data?.rolePreview) {
    throw new Error('Role preview activation failed');
  }
  return {
    rolePreview: envelope.data.rolePreview,
    products: envelope.data.products ?? [],
  };
}

/** Exit server-side role preview and restore real permissions. */
export async function clearSessionRolePreview(): Promise<AuthProductAccess[]> {
  const envelope = await authRequest<{
    rolePreview: null;
    products: AuthProductAccess[];
  }>('/auth/me/role-preview', {
    method: 'DELETE',
    auth: true,
  });
  return envelope.data?.products ?? [];
}

/** Profile + permissions for session bootstrap (login and app init). */
export async function loadSessionContext(): Promise<{
  profile: MeResponse['user'];
  products: AuthProductAccess[];
  rolePreview: MyPermissionsResponse['rolePreview'];
}> {
  const profile = await fetchCurrentUser();
  let products: AuthProductAccess[] = [];
  let rolePreview: MyPermissionsResponse['rolePreview'] = null;
  try {
    const perms = await fetchMyPermissions();
    products = perms.products;
    rolePreview = perms.rolePreview ?? null;
  } catch {
    products = [];
  }

  const hrm = products.find((p) => p.code.toUpperCase() === HRM_PRODUCT_CODE);
  if (!hrm?.permissions?.length) {
    const legacy = legacyPermissionsFromJwt(localStorage.getItem(AUTH_STORAGE_KEYS.accessToken));
    if (legacy.length > 0) {
      products = legacy;
    }
  }

  return { profile, products, rolePreview };
}

/** Re-fetch permission codes after token refresh or role changes. */
export async function refreshSessionPermissions(): Promise<AuthProductAccess[]> {
  const { useAuthStore } = await import('@/stores/auth-store');
  let products: AuthProductAccess[] = [];
  let rolePreview: MyPermissionsResponse['rolePreview'] = null;
  try {
    const perms = await fetchMyPermissions();
    products = perms.products;
    rolePreview = perms.rolePreview ?? null;
  } catch {
    products = legacyPermissionsFromJwt(localStorage.getItem(AUTH_STORAGE_KEYS.accessToken));
  }
  const store = useAuthStore.getState();
  store.syncPermissions(products);
  if (rolePreview) {
    store.setRolePreview({
      roleId: rolePreview.orgRoleId,
      roleName: rolePreview.roleName,
      levelId: rolePreview.orgRoleLevelId,
      levelName: rolePreview.levelName,
      permissions: rolePreview.permissions,
      roleCode: rolePreview.roleCode,
    });
  }
  // Do not clear local preview when the server returns null — an in-flight
  // refresh (tab focus / token refresh) can race with a just-activated preview.
  return products;
}

export async function logoutCurrentSession(): Promise<void> {
  const refresh = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
  try {
    await authRequest<null>('/auth/logout', {
      method: 'POST',
      body: JSON.stringify(refresh ? { refreshToken: refresh } : {}),
    });
  } finally {
    clearAuthStorage();
  }
}

export async function logoutAllSessions(): Promise<void> {
  try {
    await authRequest<{ success: boolean }>('/auth/logout-all', {
      method: 'POST',
      auth: true,
      sessionHeader: true,
    });
  } finally {
    clearAuthStorage();
  }
}

export async function fetchActiveSessions(): Promise<AuthSession[]> {
  /** Auth Service: one row per refresh token (signed-in device); activity in `lastUsedAt` / refresh. */
  const envelope = await authRequest<AuthSession[]>('/auth/sessions', {
    auth: true,
    sessionHeader: true,
  });
  return envelope.data ?? [];
}

export async function fetchSecurityDashboard(params?: {
  days?: number;
  activityLimit?: number;
}): Promise<SecurityDashboard> {
  const q = new URLSearchParams();
  if (params?.days != null) q.set('days', String(params.days));
  if (params?.activityLimit != null) q.set('activityLimit', String(params.activityLimit));
  const suffix = q.toString() ? `?${q}` : '';
  const envelope = await authRequest<SecurityDashboard>(`/auth/security-dashboard${suffix}`, {
    auth: true,
    sessionHeader: true,
  });
  if (!envelope.data) {
    throw new Error('Security dashboard returned no data');
  }
  return envelope.data;
}

export async function trustCurrentDevice(label?: string): Promise<void> {
  await authRequest('/auth/trusted-devices', {
    method: 'POST',
    auth: true,
    sessionHeader: true,
    body: JSON.stringify({
      label: label?.trim() || undefined,
      productCode: HRM_PRODUCT_CODE,
    }),
  });
}

export async function removeTrustedDevice(deviceId: string): Promise<void> {
  await authRequest<{ success: boolean }>(
    `/auth/trusted-devices/${encodeURIComponent(deviceId)}`,
    { method: 'DELETE', auth: true, sessionHeader: true },
  );
}

export async function logoutSession(sessionId: string): Promise<void> {
  await authRequest<{ success: boolean }>(
    `/auth/sessions/${encodeURIComponent(sessionId)}/revoke`,
    { method: 'POST', auth: true, sessionHeader: true },
  );

  const current = localStorage.getItem(AUTH_STORAGE_KEYS.sessionId);
  if (current === sessionId) {
    clearAuthStorage();
  }
}

export function hasStoredSession(): boolean {
  return Boolean(localStorage.getItem(AUTH_STORAGE_KEYS.accessToken));
}
